import { useState } from "react";
import { Button, FormControl, FormControlLabel, Radio, RadioGroup, styled, Typography } from "~/common/mui";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import Modal, { ModalSize } from "~/common/components/Modal";
import { TextField } from "~/common/mui/TextField/TextField";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { getUserStatus } from "~/features/lms/utils/lmsUtils";
import { LmsUserStatus } from "~/features/lms/types/LmsUserStatus";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { deleteMyAccount } from "~/features/myAccount/profile/api/deleteMyAccount";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { HOMEPAGE } from "~/utils/url/staticUrls";
import { invalidateNavigationCache } from "~/features/navigation/redux/navigationThunks";

export default function DeleteAccount() {
  const [deleteAlertPopup, setDeleteAlertPopup] = useState(false);
  const [deleteFeedbackPopup, setDeleteFeedbackPopup] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const userStatus = getUserStatus();

  const [selectedReason, setSelectedReason] = useState("feature.deleteAccount.reason1");
  const [feedbackText, setFeedbackText] = useState("");

  const handleDelete = async () => {
    const data = {
      reasonForDeletion: selectedReason,
      improvementFeedback: feedbackText.trim(),
    };

    try {
      const res = await deleteMyAccount(data);
      if (res.ok) {
        window.sessionStorage.setItem("accountDeleted", "true");
        setDeleteFeedbackPopup(false);
        invalidateNavigationCache();
        redirectToUrl(HOMEPAGE);
      }
    } catch (error) {
      setShowSnackbar(true);
    }
  };

  const { t } = useTranslation();

  return (
    <>
      <Container>
        <Typography variant="headlineLarge" component="b">
          {t("feature.deleteAccount.title")}
        </Typography>
        <Typography variant="bodyLarge">{t("feature.deleteAccount.subTitle")}</Typography>
        <DeleteButton variant="text" color="error" className="deleteBtn" onClick={() => setDeleteAlertPopup(true)}>
          {t("feature.deleteAccount.title")}
        </DeleteButton>
      </Container>

      {deleteAlertPopup && (
        <Modal
          title={t("feature.deleteAccount.modal.title")}
          size={ModalSize.SMALL}
          onClose={() => setDeleteAlertPopup(false)}
          footerChildren={
            <Footer>
              <ButtonStyled
                variant="text"
                onClick={() => {
                  setDeleteAlertPopup(false);
                  setDeleteFeedbackPopup(true);
                }}
              >
                {t("feature.deleteAccount.continue")}
              </ButtonStyled>
              <ButtonStyled onClick={() => setDeleteAlertPopup(false)}>{t("feature.deleteAccount.keep")}</ButtonStyled>
            </Footer>
          }
        >
          <ModalContent>
            <Typography variant="bodyMedium">{parse(t("feature.deleteAccount.modal.subTitle"))}</Typography>
            {userStatus === LmsUserStatus.Approved && (
              <>
                <Typography variant="bodyMedium" component="b">
                  {t("feature.deleteAccount.modal.section")}
                </Typography>
                <RowContent>
                  <Icon className={"icon-margin"} type={IconType.Close} />
                  <Typography variant="bodyMedium">{t("feature.deleteAccount.modal.training")}</Typography>
                </RowContent>
              </>
            )}
          </ModalContent>
        </Modal>
      )}

      {deleteFeedbackPopup && (
        <Modal
          title={t("feature.deleteAccount.title")}
          size={ModalSize.SMALL}
          onClose={() => setDeleteFeedbackPopup(false)}
          footerChildren={
            <Footer>
              <ButtonStyled variant="text" color="error" onClick={handleDelete}>
                {t("feature.deleteAccount.title")}
              </ButtonStyled>
              <ButtonStyled onClick={() => setDeleteFeedbackPopup(false)}>
                {t("feature.deleteAccount.keep")}
              </ButtonStyled>
            </Footer>
          }
        >
          <FormStyled>
            <Typography variant="bodyMedium">{t("feature.deleteAccount.feedbackModal.subTitle")}</Typography>
            <Typography variant="bodyLarge" component="b">
              {t("feature.deleteAccount.feedbackModal.section")}
            </Typography>
            <RadioGroupStyled
              className="radioGroup"
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              <FormControlLabelStyled
                className="radio"
                value="feature.deleteAccount.reason1"
                control={<Radio />}
                label={t("feature.deleteAccount.reason1")}
              />
              <FormControlLabelStyled
                className="radio"
                value="feature.deleteAccount.reason2"
                control={<Radio />}
                label={t("feature.deleteAccount.reason2")}
              />
              <FormControlLabelStyled
                className="radio"
                value="feature.deleteAccount.reason3"
                control={<Radio />}
                label={t("feature.deleteAccount.reason3")}
              />
            </RadioGroupStyled>
            <Typography variant="bodyLarge" component="b">
              {t("feature.deleteAccount.feedbackModal.inputLabel")}
            </Typography>

            <TextField
              size="medium"
              fullWidth={true}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={t("feature.deleteAccount.feedbackModal.description")}
            />
          </FormStyled>
          <Snackbar
            open={showSnackbar}
            onClose={() => setShowSnackbar(false)}
            action={<SnackbarCloseButton onClick={() => setShowSnackbar(false)} />}
            message={t("myAccount.dataLoadError")}
          />
        </Modal>
      )}
    </>
  );
}

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "24px",
  alignSelf: "stretch",
}));

const DeleteButton = styled(Button)({
  alignSelf: "flex-end",
});

const RowContent = styled("div")({
  display: "flex",
  gap: "8px",
  alignItems: "center",
});

const RadioGroupStyled = styled(RadioGroup)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
});

const FormControlLabelStyled = styled(FormControlLabel)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  ...getFontStyle(FontSize.BodyLarge),
});

const FormStyled = styled(FormControl)({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
});

const Footer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: "8px",
  [theme.breakpoints.down("tablet")]: {
    width: "100%",
    justifyContent: "space-between",
  },
}));

const ButtonStyled = styled(Button)(({ theme }) => ({
  [theme.breakpoints.down("tablet")]: {
    width: "100%",
  },
}));

const ModalContent = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
}));
